import {
  useGetSalesRepListFiltersQuery,
  useLazyGetSalesRepsQuery,
  useUpdateSalesRepMutation,
} from '@/services/salesReps';
import { Badge, Box, Button, Pagination, Table, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, NavLink, Outlet, useSearchParams } from 'react-router-dom';

import { AppliedDataFilter, SalesRep, SalesRepStatus, salesRepStatusLabels, UserPermissions } from 'types';

import { AccountsListLoading } from '../AccountsListLoading';
import RequirePermissions from '../auth/requirePermissions';
import { FilterBar } from '../filters';
import { AddFilterButtonDropdown } from '../filters/AddFilterButton';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';
import { SkeletonBox } from '../SkeletonBox';

import { ChangeSalesRepStatusModal } from './ChangeSalesRepStatusModal';
import { SalesRepActionModal } from './SalesRepActionModal';
import { salesRepStatusBadgeVariant } from './statusBadge';
import { TerminateSalesRepModal } from './TerminateSalesRepModal';

type RowActionsProps = {
  row: SalesRep;
  flagEnabled: boolean;
  onLegacyOpen: (newStatus: string, row: SalesRep) => void;
  onChangeStatus: (row: SalesRep) => void;
  onTerminate: (row: SalesRep) => void;
};

const LEGACY_BLOCKED_STATUSES = new Set([SalesRepStatus.blocked, SalesRepStatus.terminated]);
const LEGACY_SUSPENDED_STATUSES = new Set([SalesRepStatus.temporarySuspend, SalesRepStatus.suspended]);

const RowActions = ({ row, flagEnabled, onLegacyOpen, onChangeStatus, onTerminate }: RowActionsProps) => {
  const handleLegacyBlock = useCallback(() => onLegacyOpen(SalesRepStatus.blocked, row), [onLegacyOpen, row]);
  const handleLegacyUnblock = useCallback(() => onLegacyOpen(SalesRepStatus.active, row), [onLegacyOpen, row]);
  const handleChangeStatus = useCallback(() => onChangeStatus(row), [onChangeStatus, row]);
  const handleTerminate = useCallback(() => onTerminate(row), [onTerminate, row]);

  if (row.status === SalesRepStatus.override) return null;

  if (flagEnabled) {
    const isTerminated = row.status === SalesRepStatus.terminated;
    return (
      <Box gap="sm" direction="row">
        <RequirePermissions permissions={[UserPermissions.reCheckAdmin]}>
          <Button size="sm" variant="secondary" onClick={handleChangeStatus}>
            Change Status
          </Button>
        </RequirePermissions>
        {!isTerminated && (
          <RequirePermissions permissions={[UserPermissions.reCheckAdmin]}>
            <Button size="sm" variant="secondary" tone="danger" onClick={handleTerminate}>
              Terminate
            </Button>
          </RequirePermissions>
        )}
      </Box>
    );
  }

  const isBlocked = LEGACY_BLOCKED_STATUSES.has(row.status);
  const isSuspendedOrBlocked = isBlocked || LEGACY_SUSPENDED_STATUSES.has(row.status);

  return (
    <Box childGap="md">
      {row.status === SalesRepStatus.active ? (
        <RequirePermissions permissions={[UserPermissions.reCheckAdmin]}>
          <Button
            iconPrefix="ban"
            size="sm"
            aria-label="Block Sales Rep"
            variant="secondary"
            onClick={handleLegacyBlock}
          />
        </RequirePermissions>
      ) : null}
      {isBlocked ? (
        <RequirePermissions permissions={[UserPermissions.reCheckAdmin]}>
          <Button
            iconPrefix="ban"
            size="sm"
            variant="secondary"
            tone="danger"
            aria-label="Unblock Sales Rep"
            onClick={handleLegacyUnblock}
          />
        </RequirePermissions>
      ) : null}
      {isSuspendedOrBlocked ? (
        <RequirePermissions permissions={[UserPermissions.reCheckAdmin]}>
          <Button
            iconPrefix="checkbox-btn-checked"
            size="sm"
            aria-label="Activate Sales Rep"
            variant="secondary"
            onClick={handleLegacyUnblock}
          />
        </RequirePermissions>
      ) : null}
    </Box>
  );
};

export const SalesReps = () => {
  return (
    <>
      <Helmet>
        <title>Manage Sales Reps</title>
      </Helmet>
      <MainContainer>
        <PageHeader title="Manage Sales Reps"></PageHeader>
        <Box direction="row" borderWidth="0 0 xs 0" borderColor="separator">
          <Box direction="row" borderWidth="0 0 xs 0" borderColor="separator">
            <NavLink to="/admin/sales-reps" style={{ textDecoration: 'none', fontWeight: '500' }} end>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  Sales Reps
                </Box>
              )}
            </NavLink>
            <NavLink to="/admin/sales-reps/history" style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  History
                </Box>
              )}
            </NavLink>
          </Box>
          <Box padding="md 0 0 0"></Box>
        </Box>
        <Outlet />
      </MainContainer>
    </>
  );
};

export const SalesRepsListView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [trigger, { data: searchResponse, isLoading, isFetching }] = useLazyGetSalesRepsQuery();
  const [updateSalesRep, { isLoading: isUpdatingSalesRep }] = useUpdateSalesRepMutation();
  const [salesRep, setSalesRep] = useState<any>();
  const [appliedFilters, setAppliedFilters] = useState<Array<any>>([]);
  const { salesReps, total } = searchResponse || { salesReps: [], total: 0 };
  const { data = [], isLoading: isFiltersLoading } = useGetSalesRepListFiltersQuery();

  const { salesRepStatusesV2 = false } = useFlags();

  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [terminateModalOpen, setTerminateModalOpen] = useState(false);
  const [selectedSalesRep, setSelectedSalesRep] = useState<SalesRep | undefined>();

  const currentPage = searchParams.get('page');
  const advancedFilters = searchParams.get('advancedFilters');
  const searchTerm = searchParams.get('search') || undefined;

  useEffect(() => {
    const pageNum = currentPage ? Number(currentPage) : 1;
    const filters = advancedFilters || JSON.stringify([]);

    setAppliedFilters(JSON.parse(filters));
    trigger({
      pageNum,
      advancedFilters: filters,
      searchTerm,
    });
  }, [trigger, currentPage, advancedFilters, searchTerm]);

  const handleApplyAdvancedFilters = useCallback(
    (filtersToApply: AppliedDataFilter[]) => {
      if (filtersToApply.some((filter) => filter.selectedValues && filter.selectedValues.length)) {
        searchParams.set('advancedFilters', JSON.stringify(filtersToApply));
        setSearchParams(searchParams);
      }
      searchParams.delete('page');
      setAppliedFilters(filtersToApply);
    },
    [setAppliedFilters, searchParams, setSearchParams],
  );

  const handleRemoveAdvancedFilter = useCallback(
    (filterId: string) => {
      const newFilters = appliedFilters.filter((filter) => filter.id !== filterId);
      if (newFilters.length) {
        searchParams.set('advancedFilters', JSON.stringify(newFilters));
      } else {
        searchParams.delete('advancedFilters');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
      setAppliedFilters(newFilters);
    },
    [appliedFilters, setAppliedFilters, searchParams, setSearchParams],
  );

  const handleClearAdvancedFilters = useCallback(() => {
    searchParams.delete('advancedFilters');
    searchParams.delete('page');
    setSearchParams(searchParams);
    setAppliedFilters([]);
  }, [setAppliedFilters, searchParams, setSearchParams]);

  const onPageChange = useCallback(
    (newPage: number) => {
      searchParams.set('page', newPage.toString());
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const onSearchChange = useCallback(
    (term: string) => {
      if (term && term !== '') {
        searchParams.set('search', term);
      } else {
        searchParams.delete('search');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const handleOpenModal = useCallback(
    (newStatus: string, rep: any) => {
      let actionToPerform;
      switch (newStatus) {
        case SalesRepStatus.active:
          actionToPerform = 'unblock';
          break;
        case SalesRepStatus.blocked:
          actionToPerform = 'block';
          break;
        case SalesRepStatus.temporarySuspend:
          actionToPerform = 'suspend';
          break;
        default:
          actionToPerform = 'block';
      }
      setSalesRep({ ...rep, actionToPerform, newStatus });
      openModal();
    },
    [openModal],
  );

  const handleCloseModal = useCallback(() => {
    setSalesRep(undefined);
    closeModal();
  }, [closeModal]);

  const handleUpdateStatus = useCallback(
    async (status: SalesRepStatus) => {
      if (!salesRep) return;
      const toastId = toast.loading(
        `${salesRep.actionToPerform.charAt(0).toUpperCase() + salesRep.actionToPerform.slice(1)}ing sales rep...`,
      );
      try {
        await updateSalesRep({ id: salesRep.id, status, reason: 'Legacy status change' });
      } catch (e: any) {
        console.error(e);
        toast.error(e?.data?.message || `An error occurred while ${salesRep.actionToPerform}ing the sales rep.`);
      } finally {
        toast.dismiss(toastId);
        handleCloseModal();
      }
    },
    [salesRep, updateSalesRep, handleCloseModal],
  );

  const handleOpenChangeStatusModal = useCallback((rep: SalesRep) => {
    setSelectedSalesRep(rep);
    setStatusModalOpen(true);
  }, []);

  const handleCloseChangeStatusModal = useCallback(() => {
    setStatusModalOpen(false);
    setSelectedSalesRep(undefined);
  }, []);

  const handleOpenTerminateModal = useCallback((rep: SalesRep) => {
    setSelectedSalesRep(rep);
    setTerminateModalOpen(true);
  }, []);

  const handleCloseTerminateModal = useCallback(() => {
    setTerminateModalOpen(false);
    setSelectedSalesRep(undefined);
  }, []);

  const handleSearchChange = useCallback((term: string) => onSearchChange(term), [onSearchChange]);

  const columns = useMemo(
    () => [
      {
        heading: 'Status',
        dataKey: 'status',
        width: 200,
        render: (cell: any) => (
          <Badge
            variant={salesRepStatusBadgeVariant[cell as SalesRepStatus] ?? 'default'}
            message={salesRepStatusLabels[cell as SalesRepStatus]}
          />
        ),
      },
      {
        heading: 'Recheck Id',
        width: 200,
        render: (_cell: any, row: any) => <Link to={`${row.id}`}>{row.idProviderUser?.idProviderId}</Link>,
      },
      {
        heading: 'Sales Rep',
        render: (_cell: any, row: any) => (
          <Box display="block" childGap="2xs" fontSize="xs" padding="sm 0" color="body-secondary">
            <Box display="block" fontWeight="medium" fontSize="sm">
              {row.idProviderUser?.displayName}
            </Box>
            {row.idProviderUser?.emails?.length && <Box>{row.idProviderUser?.emails[0]}</Box>}
          </Box>
        ),
      },
      {
        heading: 'Total # Emails (LightReach)',
        width: 120,
        headerClassName: 'wrap',
        render: (_cell: any, row: any) => row.accountEmails?.length,
      },
      {
        heading: 'Total # Emails (Recheck)',
        width: 120,
        headerClassName: 'wrap',
        render: (_cell: any, row: any) => row.idProviderUser?.emails?.length,
      },
      {
        heading: 'Total # Orgs (LightReach)',
        width: 120,
        headerClassName: 'wrap',
        render: (_cell: any, row: any) => row.organizationIds?.length,
      },
      {
        heading: 'Actions',
        width: salesRepStatusesV2 ? 220 : 80,
        render: (_cell: any, row: any) => (
          <RowActions
            row={row as SalesRep}
            flagEnabled={salesRepStatusesV2}
            onLegacyOpen={handleOpenModal}
            onChangeStatus={handleOpenChangeStatusModal}
            onTerminate={handleOpenTerminateModal}
          />
        ),
      },
    ],
    [salesRepStatusesV2, handleOpenModal, handleOpenChangeStatusModal, handleOpenTerminateModal],
  );

  return (
    <>
      {isLoading ? (
        <Box height="52px" alignItems="center" direction="row" childGap="xl">
          <SkeletonBox width="80px" height="14px" />
          <SkeletonBox width="170px" height="14px" />
          <SkeletonBox width="80px" height="14px" />
          <SkeletonBox width="100px" height="14px" />
        </Box>
      ) : (
        <>
          <Box margin="0 lg 0 lg">
            <FilterBar
              filtersState={{
                searchTerm,
              }}
              handleSearchTerm={handleSearchChange}
              placeholder="Search name, email, Recheck Id..."
            >
              {!isFiltersLoading && (
                <AddFilterButtonDropdown
                  dataFilters={data}
                  appliedFilters={appliedFilters}
                  onRemoveFilter={handleRemoveAdvancedFilter}
                  onApplyFilters={handleApplyAdvancedFilters}
                  onClearFilters={handleClearAdvancedFilters}
                />
              )}
            </FilterBar>
            {isFetching || isLoading ? (
              <AccountsListLoading rows={12} />
            ) : (
              <Table rowKey="id" columns={columns} rows={salesReps} isScrollable={{ x: true }} />
            )}
          </Box>
          {salesReps?.length > 0 && (
            <Pagination
              activePage={Number(currentPage) || 1}
              arePagesVisible
              itemsPerPage={20}
              numberOfPagesDisplayed={3}
              onChange={onPageChange}
              totalItemsCount={total ?? 0}
              isCompact
            />
          )}
        </>
      )}
      {!salesRepStatusesV2 && (
        <SalesRepActionModal
          isModalOpen={isModalOpen}
          salesRep={salesRep}
          handleCloseModal={handleCloseModal}
          handleUpdateStatus={handleUpdateStatus}
          isLoading={isLoading || isUpdatingSalesRep}
        />
      )}
      {salesRepStatusesV2 && statusModalOpen && (
        <ChangeSalesRepStatusModal
          isOpen={statusModalOpen}
          salesRep={selectedSalesRep}
          onClose={handleCloseChangeStatusModal}
        />
      )}
      {salesRepStatusesV2 && terminateModalOpen && (
        <TerminateSalesRepModal
          isOpen={terminateModalOpen}
          salesRep={selectedSalesRep}
          onClose={handleCloseTerminateModal}
        />
      )}
    </>
  );
};
